import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorNotification, PrimaryButton } from '@cb/apricot-react';
import { CircularProgress, Grid } from '@mui/material';

import { useMyDIForms } from 'src/di/apis';
import { AIFormCard, Typography } from 'src/components';
import { APP_URLS } from 'src/constants';
import { useAuth } from 'src/contexts';
import { getFile } from 'src/services/schoolRequest';
import { deleteInstitution } from 'src/di/apis/deleteInstitution';
import { ISubmitHistory, ISummary, TAIForm } from 'src/types/aiForm';

const DIHome: FC = () => {
  const navigate = useNavigate();
  const { userName } = useAuth();

  const { data: diForms, error, loading, loaded, fetch } = useMyDIForms({ lazy: false });

  const [deleteError, setDeleteError] = useState<Nullable<string>>(null);
  const [fetchSchoolError, setFetchSchoolError] = useState<Nullable<string>>(null);

  const handleClickStartNewRequest = useCallback(() => {
    navigate(APP_URLS.DISEARCH);
  }, [navigate]);

  const handleDeleteSchool = useCallback(
    async (school: ISummary) => {
      try {
        await deleteInstitution(school.categoryId);
        setTimeout(fetch, 3000);
      } catch (error: any) {
        setDeleteError(error.message);
      }
    },
    [fetch],
  );

  const navigateToSchool = useCallback(
    async (school: ISummary) => {
      try {
        if (school.requestType === 'create') {
          navigate(APP_URLS.DIFORM, {
            state: {
              summary: school
            }
          });
        } else if (school.requestType === 'update') {
          if (!school.submission) throw new Error("No submission could be located.");
          navigate(APP_URLS.DIUPDATEFORM, {
            state: {
              summary: school,
              source: 'HOME'
            }
          });
        }
      } catch (error: any) {
        setFetchSchoolError(error.message);
      }
    },
    [navigate],
  );

  //Update and Submit
  const handleClickUpdateAndSubmit = useCallback(
    async (school: ISummary) => {
      if (school.requestType === 'create') {
        navigate(APP_URLS.DIFORM, {
          state:
            { summary: school }
        });
      } else {
        navigate(APP_URLS.DIUPDATEFORM, {
          state:
          {
            summary: school,
            source: 'HOME'
          }
        });
      }
    },
    [navigate], //navigateToSchool
  );

  //Update and Re-Submit
  const handleClickReSubmitSchool = useCallback(
    async (school: ISummary) => {
      try {
        await navigateToSchool(school);
      } catch (error: any) {
        setFetchSchoolError(error.message);
      }
    },
    [navigateToSchool],
  );

  //View
  const handleClickViewSchool = useCallback(
    async (school: ISummary) => {
      try {
        await navigateToSchool(school);
      } catch (error: any) {
        setFetchSchoolError(error.message);
      }
    },
    [navigateToSchool],
  );

  //Submission History
  const handleClickViewSchoolSubmit = useCallback(
    async (school: ISummary, submit: ISubmitHistory) => {
      try {
        if (school.requestType === 'create') {
          navigate(APP_URLS.DIFORM, {
            state: {
              summary: school,
              file: submit.submission
            }
          });
        } else if (school.requestType === 'update') {
          if (!school.submission) throw new Error("No submission could be located.");
          navigate(APP_URLS.DIUPDATEFORM, {
            state: {
              summary: school,
              file: submit.submission,
              source: 'HOME'
            }
          });
        }
      } catch (error: any) {
        setFetchSchoolError(error.message);
      }
    },
    [navigate],
  );

  const handleRefresh = useCallback(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (loaded && !diForms.length) {
      navigate(APP_URLS.DISEARCH);
    }
  }, [loaded, navigate, diForms]);

  const errorMessage = error || deleteError || fetchSchoolError;

  return (
    <Grid container spacing={2}>
      {<>
        {loading && (
          <Grid container item xs={12} justifyContent="center">
            <CircularProgress color="secondary" />
          </Grid>
        )}

        {errorMessage && (
          // @ts-ignore
          <ErrorNotification dismissible title="Error">
            {errorMessage}
          </ErrorNotification>
        )}

        <Grid item xs={12}>
          <Typography variant="h4">Welcome, {userName}</Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1">
          Please allow approximately <b>7 -10 business days for processing for each submission </b>of your request. <br /> <b>Please note:</b>Please be sure to complete the form in its entirety and include any supporting documentation to expedite processing. <b>The most up to date status of your application is displayed in the "Status" section below.</b>
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <PrimaryButton style={{ width: '100%' }} onClick={handleClickStartNewRequest}>
            Search to start a request
          </PrimaryButton>
        </Grid>
        {diForms.map((school) => (
          <Grid item xs={12} key={school.categoryId}>
            <AIFormCard
              school={school}
              onClickDelete={() => handleDeleteSchool(school)}
              onClickUpdateAndSubmit={() => handleClickUpdateAndSubmit(school)}
              onClickUpdateAndReSubmit={() => handleClickReSubmitSchool(school)}
              onClickView={() => handleClickViewSchool(school)}
              onClickViewSubmit={(submit) => handleClickViewSchoolSubmit(school, submit)}
              onRefresh={handleRefresh}
            />
          </Grid>
        ))}
      </>
      }
    </Grid>
  );
};

export default DIHome;
